import React, { useState, useRef, useEffect, useCallback } from 'react';
import QuizModule from './QuizModule';
import Logo from '../../assets/logo.svg';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import { ToastContainer } from 'react-toastify';
import SegmentedProgress from './Components/SegmentedProgress';
import StickyFooter from './Components/StickyFooter';
import { Icon } from '../../Components/elements/icon';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetClassmedResultQuery,
  useGetIncompleteQuizQuery,
  useGetQuestionsQuery,
} from '../../features/quizApiSlice';
import {
  getClassmedIdFromSearch,
  getClassmedPharmacyIdFromSearch,
  getClassmedResultPath,
  isClassmedMode,
  isClassmedRetakeFromSearch,
  saveClassmedId,
  saveClassmedPharmacyId,
  saveClassmedResult,
} from '../../utils/classmed';
import ClassmedInvalidLink from '../Classmed/ClassmedInvalidLink';
import QuizResolvingLoader from './Components/QuizResolvingLoader';

const areQuizStatesEqual = (prev, next) =>
  prev.currentSlideCode === next.currentSlideCode &&
  prev.quizData === next.quizData &&
  prev.slideNavigation === next.slideNavigation &&
  prev.slideValues === next.slideValues &&
  prev.isLastSlide === next.isLastSlide &&
  prev.lang === next.lang &&
  prev.hasChoiceNext === next.hasChoiceNext &&
  prev.hasChoiceNextSelection === next.hasChoiceNextSelection &&
  prev.hasInsight === next.hasInsight &&
  prev.canContinue === next.canContinue &&
  prev.currentPositionCode === next.currentPositionCode;

const Quiz = () => {
  const quizModuleRef = useRef();
  const isClassmed = isClassmedMode();
  const classmedId = getClassmedIdFromSearch();
  const classmedPharmacyId = getClassmedPharmacyIdFromSearch();
  const isClassmedRetake = isClassmedRetakeFromSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resumeQuizId = !isClassmed ? searchParams.get('quizId') : '';
  const hasHandledCompletedResumeRef = useRef(false);
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [progressValue, setProgressValue] = useState(0);

  const selectedLang = useSelector(selectCurrentLanguage);
  const { t, i18n } = useTranslation();
  const resolvedLang = isClassmed ? 'fr' : selectedLang === 'us' ? 'en' : selectedLang || 'en';
  const [lang, setLang] = useState(resolvedLang);

  const [quizState, setQuizState] = useState({
    currentSlideCode: null,
    quizData: [],
    slideNavigation: {},
    slideValues: {},
    isLastSlide: false,
    lang: 'en',
    hasChoiceNext: false,
    hasChoiceNextSelection: false,
    hasInsight: false,
    canContinue: true,
  });
  const { data: existingClassmedResult, isFetching: isCheckingClassmedResult } = useGetClassmedResultQuery(
    { classmedId, lang: 'fr' },
    { skip: !isClassmed || !classmedId || isClassmedRetake },
  );
  const {
    data: resumeQuizResponse,
    isFetching: isFetchingResumeQuiz,
    isLoading: isLoadingResumeQuiz,
    isError: isResumeQuizError,
  } = useGetIncompleteQuizQuery(resumeQuizId, {
    skip: !resumeQuizId,
  });
  const { data: resumeQuestionsResponse } = useGetQuestionsQuery(resolvedLang, {
    skip: !resumeQuizId || isClassmed,
  });
  const resumeQuiz = resumeQuizResponse?.data || null;
  const hasResumeQuiz = !!resumeQuiz?.id;
  const isInvalidResumeQuiz = !!resumeQuizId && !!resumeQuizResponse && !hasResumeQuiz;
  const isCompletedResumeQuiz = hasResumeQuiz && Number(resumeQuiz.progressBar) === 100 && Number(resumeQuiz.quiz_id) > 0;

  useEffect(() => {
    setLang(resolvedLang);
  }, [resolvedLang]);

  useEffect(() => {
    if (!isClassmed) return;
    i18n.changeLanguage('fr');
  }, [i18n, isClassmed]);

  useEffect(() => {
    if (!isClassmed || !classmedId) return;
    saveClassmedId(classmedId);
    saveClassmedPharmacyId(classmedPharmacyId);
  }, [classmedId, classmedPharmacyId, isClassmed]);

  useEffect(() => {
    if (!isClassmed || isClassmedRetake || !classmedId || !existingClassmedResult) return;

    const storedResult = saveClassmedResult(existingClassmedResult, classmedId, 'fr') || existingClassmedResult;
    navigate(getClassmedResultPath(classmedId, { pharmacyId: classmedPharmacyId }), {
      replace: true,
      state: {
        result: storedResult,
        returningUser: true,
      },
    });
  }, [classmedId, classmedPharmacyId, existingClassmedResult, isClassmed, isClassmedRetake, navigate]);

  useEffect(() => {
    hasHandledCompletedResumeRef.current = false;
  }, [resumeQuizId]);

  useEffect(() => {
    if (!isCompletedResumeQuiz || hasHandledCompletedResumeRef.current) return;
    hasHandledCompletedResumeRef.current = true;

    localStorage.setItem('quiz_id', resumeQuiz.quiz_id);
    if (resumeQuiz.email) localStorage.setItem('email', resumeQuiz.email);

    navigate(`/product-suggestion${location.search}`, {
      replace: true,
      state: {
        returningUser: true,
        quiz_id: resumeQuiz.quiz_id,
      },
    });
  }, [isCompletedResumeQuiz, location.search, navigate, resumeQuiz]);

  useEffect(() => {
    if (!isInvalidResumeQuiz) return;
    navigate('/quiz', { replace: true });
  }, [isInvalidResumeQuiz, navigate]);

  const handleQuizStateChange = useCallback((nextState) => {
    setQuizState((prevState) => (areQuizStatesEqual(prevState, nextState) ? prevState : nextState));
  }, []);

  const handleBack = () => {
    if (quizModuleRef.current) {
      quizModuleRef.current.handlePrevious();
    }
  };

  const handleContinue = () => {
    if (quizModuleRef.current) {
      quizModuleRef.current.handleContinue();
    }
  };

  const backLabel = t('Quiz_Back');
  const continueLabel = quizState.isLastSlide ? t('Quiz_GetMyFormula') : t('Quiz_Continue');

  const showBack = quizState.currentSlideCode != null && quizState.currentSlideCode !== 1;

  if (isClassmed && (!classmedId || !classmedPharmacyId)) {
    return <ClassmedInvalidLink />;
  }

  if (isClassmed && classmedId && !isClassmedRetake && isCheckingClassmedResult) {
    return (
      <div
        className="t-h-[100dvh] t-flex t-flex-col t-bg-surface t-font-sans t-text-navy t-overflow-hidden"
        style={{ WebkitFontSmoothing: 'antialiased' }}
      >
        <div className="t-z-[100] t-bg-surface/[.95] t-backdrop-blur-[14px] t-border-b t-border-g200">
          <div className="t-flex t-items-center t-justify-center t-px-4 t-pt-2.5 t-pb-2 t-relative t-min-h-[46px]">
            <img src={Logo} alt="" width={90} height={90} className="t-mx-auto" />
          </div>
        </div>
        <div className="t-flex t-flex-1 t-items-center t-justify-center t-px-5">
          <p className="!t-m-0 t-text-center t-text-base t-font-semibold t-text-navy/70">
            {t('Classmed_CheckingResult')}
          </p>
        </div>
      </div>
    );
  }

  if (
    resumeQuizId &&
    !isResumeQuizError &&
    (isLoadingResumeQuiz || isFetchingResumeQuiz || isCompletedResumeQuiz || isInvalidResumeQuiz || !resumeQuizResponse)
  ) {
    return <QuizResolvingLoader />;
  }

  return (
    <div
      className="t-h-[100dvh] t-flex t-flex-col t-bg-surface t-font-sans t-text-navy t-overflow-hidden"
      style={{ WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="t-z-[100] t-bg-surface/[.95] t-backdrop-blur-[14px] t-border-b t-border-g200">
        <div className="t-flex t-items-center t-justify-center t-px-4 t-pt-2.5 t-pb-2 t-relative t-min-h-[46px]">
          {showBack && (
            <button
              type="button"
              className="t-absolute t-left-2.5 t-top-1/2 -t-translate-y-1/2 t-flex t-items-center t-gap-[5px] t-bg-transparent t-border-0 t-cursor-pointer t-text-g300 t-font-sans t-text-sm lg:text-base t-font-bold t-tracking-wider t-uppercase t-py-1.5 t-px-2 t-rounded-lg hover:t-text-navy hover:t-bg-g100"
              onClick={handleBack}
            >
              <Icon name="backCircle" size={20} raw className="t-mr-1" /> {backLabel}
            </button>
          )}
          <img src={Logo} alt="" width={90} height={90} className="t-mx-auto" />
          {showProgressBar && (
            <span className="t-absolute t-right-3.5 t-top-1/2 -t-translate-y-1/2 t-text-sm lg:text-lg t-font-semibold t-text-g300 t-tracking-wide">
              {progressValue}%
            </span>
          )}
        </div>
        {showProgressBar && quizState.quizData.length > 0 && (
          <SegmentedProgress
            currentSlideCode={quizState.currentSlideCode}
            quizData={quizState.quizData}
            slideNavigation={quizState.slideNavigation}
            lang={lang}
          />
        )}
      </div>

      <div className="t-flex-1 t-min-h-0 t-overflow-y-auto t-overscroll-contain t-pb-[108px]">
        <QuizModule
          setProgressValue={setProgressValue}
          ref={quizModuleRef}
          setShowProgressBar={setShowProgressBar}
          progressValue={progressValue}
          onStateChange={handleQuizStateChange}
          isClassmedMode={isClassmed}
          classmedId={classmedId}
          classmedPharmacyId={classmedPharmacyId}
          forcedLang={isClassmed ? 'fr' : ''}
          initialIncompleteQuiz={hasResumeQuiz ? resumeQuiz : null}
          initialQuestions={resumeQuestionsResponse?.data || null}
        />
      </div>

      {!quizState.isLastSlide && (quizState.currentSlideCode != null || quizState.quizData.length > 0) && (
        <StickyFooter
          label={continueLabel}
          disabled={
            !quizState.canContinue ||
            (!!quizState.hasChoiceNext && !quizState.hasInsight && !quizState.hasChoiceNextSelection)
          }
          onClick={handleContinue}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default Quiz;
